import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Chip,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { ClaimVerdict, EvidenceMass, EvidenceSource } from "../../../../types/claims";
import { Caption } from "../../../primitives/data-display/Caption";
import { VerdictBalance } from "../VerdictBalance";
import { VerdictScale } from "../VerdictScale";
import { VerdictDot } from "../VerdictDot";
import {
  QUALITY_RATING_LABEL_KEYS,
  qualityRatingBucket,
} from "../../../../lib/qualityRatingBucket";
import { RequireFeatureFlag } from "../../../gate/RequireFeatureFlag";
import { FeatureFlags } from "../../../../lib/delivery_keys";

interface FindingsCardProps {
  verdict: ClaimVerdict;
  /** Aggregated signal on the roughly [-1, 1] contradicted-to-supported axis. */
  signal: number;
  /** Summed source weight on each side, shown under the scale as the verdict's balance. */
  evidenceMass: EvidenceMass;
  evidence: EvidenceSource[];
  /** Papers the search found relevant to the claim, of which `evidence` are the ones judged. */
  sourcesScanned: number;
  /** Fallacy tags identified in the claim's reasoning. Pass [] when none. */
  fallacyTags?: string[];
  /** Tag → plain-language definition lookup, used to populate chip tooltips.
   *  Server-hydrated from the controlled vocabulary. */
  fallacyDefinitions?: Record<string, string>;
  /** True while the fallacy API call is in-flight; renders skeleton chips. */
  isFallacyLoading?: boolean;
}

const VerdictHeaderRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 16,
});

const SmallInfoIcon = styled(InfoOutlinedIcon)(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.text.disabled,
  cursor: "help",
}));

const ScaleCaption = styled(Caption)(({ theme }) => ({
  display: "block",
  marginBottom: theme.spacing(1),
}));

const FallacyDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const FallacyCaption = styled(Caption)(({ theme }) => ({
  display: "block",
  marginBottom: theme.spacing(1),
}));

const SkeletonRow = styled("div")({ display: "flex", gap: 6 });

const FallacyChipRow = styled("div")({ display: "flex", flexWrap: "wrap", gap: 6 });

const HelpCursorChip = styled(Chip)({ cursor: "help", fontWeight: 500 });

const MetricsGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 10,
});

const MetricPaper = styled(Paper)({ backgroundColor: "rgba(0,0,0,0.05)" });

const MetricValueRow = styled("div")({ display: "flex", alignItems: "baseline", gap: 4 });

export function FindingsCard({
  verdict,
  signal,
  evidenceMass,
  evidence,
  sourcesScanned,
  fallacyTags = [],
  fallacyDefinitions = {},
  isFallacyLoading = false,
}: FindingsCardProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const labels: Record<ClaimVerdict, string> = {
    SUPPORTED: t("verdicts.SUPPORTED"),
    LEANING_SUPPORTED: t("verdicts.LEANING_SUPPORTED"),
    LEANING_CONTRADICTED: t("verdicts.LEANING_CONTRADICTED"),
    MIXED: t("verdicts.MIXED"),
    CONTRADICTED: t("verdicts.CONTRADICTED"),
    INCONCLUSIVE: t("verdicts.INCONCLUSIVE"),
    INSUFFICIENT: t("verdicts.INSUFFICIENT"),
    NO_COVERAGE: t("verdicts.NO_COVERAGE"),
    PARTIALLY_SUPPORTED: t("verdicts.PARTIALLY_SUPPORTED"),
  };

  const tooltips: Partial<Record<ClaimVerdict, string>> = {
    SUPPORTED: t("verdicts.SUPPORTED_tooltip"),
    LEANING_SUPPORTED: t("verdicts.LEANING_SUPPORTED_tooltip"),
    LEANING_CONTRADICTED: t("verdicts.LEANING_CONTRADICTED_tooltip"),
    CONTRADICTED: t("verdicts.CONTRADICTED_tooltip"),
    INCONCLUSIVE: t("verdicts.INCONCLUSIVE_tooltip"),
    INSUFFICIENT: t("verdicts.INSUFFICIENT_tooltip"),
    NO_COVERAGE: t("verdicts.NO_COVERAGE_tooltip"),
    PARTIALLY_SUPPORTED: t("verdicts.PARTIALLY_SUPPORTED_tooltip"),
  };

  const verdictColor = theme.verdicts[verdict].main;

  const totalPapers = evidence.length;
  const avgCrossDesignQuality =
    totalPapers > 0
      ? evidence.reduce((sum, e) => sum + e.cross_design_quality, 0) / totalPapers
      : null;
  const qualityBucket =
    avgCrossDesignQuality !== null ? qualityRatingBucket(avgCrossDesignQuality) : null;
  const contradictCount = evidence.filter((e) => e.verdict === "CONTRADICTS").length;

  const metrics = [
    { label: t("result.papersReviewed"), value: sourcesScanned, suffix: null },
    {
      label: t("result.avgMethodology"),
      value: qualityBucket !== null ? t(QUALITY_RATING_LABEL_KEYS[qualityBucket]) : "—",
      suffix: null,
    },
    {
      label: t("result.contradictClaim"),
      value: contradictCount,
      suffix: totalPapers > 0 ? `of ${totalPapers}` : null,
    },
  ];

  const hasDisagreement = evidenceMass.support > 0 && evidenceMass.contradict > 0;
  const hasFallacies = fallacyTags.length > 0;
  const showFallacySection = isFallacyLoading || hasFallacies;

  return (
    <Stack gap={1}>
      <Paper>
        <VerdictHeaderRow>
          <VerdictDot $color={verdictColor} $size={10} />
          <Typography variant="body1" fontWeight={500} data-testid="verdict-label">
            {labels[verdict]}
          </Typography>
          {tooltips[verdict] && (
            <Tooltip title={tooltips[verdict]} arrow placement="top">
              <SmallInfoIcon />
            </Tooltip>
          )}
        </VerdictHeaderRow>

        <ScaleCaption color="textDisabled">Where this verdict sits</ScaleCaption>

        <VerdictScale verdict={verdict} signal={signal}>
          {hasDisagreement && (
            <RequireFeatureFlag flag={FeatureFlags.verdictBalance}>
              <VerdictBalance mass={evidenceMass} />
            </RequireFeatureFlag>
          )}
        </VerdictScale>

        {showFallacySection && (
          <>
            <FallacyDivider />
            <FallacyCaption>{t("fallacy.label")}</FallacyCaption>
            {isFallacyLoading ? (
              <SkeletonRow>
                <Skeleton variant="rounded" width={80} height={24} />
                <Skeleton variant="rounded" width={110} height={24} />
                <Skeleton variant="rounded" width={64} height={24} />
              </SkeletonRow>
            ) : (
              <FallacyChipRow>
                {fallacyTags.map((tag) => {
                  const friendly = t(`fallacy.tags.${tag}`, { defaultValue: tagToFriendly(tag) });
                  const definition = fallacyDefinitions[tag] ?? "";
                  return (
                    <Tooltip key={tag} title={definition} arrow placement="top" enterTouchDelay={0}>
                      <HelpCursorChip label={friendly} size="small" tabIndex={0} />
                    </Tooltip>
                  );
                })}
              </FallacyChipRow>
            )}
          </>
        )}
      </Paper>

      <MetricsGrid>
        {metrics.map(({ label, value, suffix }) => (
          <MetricPaper key={label}>
            <Caption>{label}</Caption>
            <MetricValueRow>
              <Caption color="textPrimary" style={{ fontWeight: 500 }}>
                {value}
              </Caption>
              {suffix && <Caption>{suffix}</Caption>}
            </MetricValueRow>
          </MetricPaper>
        ))}
      </MetricsGrid>
    </Stack>
  );
}

function tagToFriendly(tag: string): string {
  const cleaned = tag.replace(/-/g, " ");
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}
